import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useSelector, useDispatch } from 'react-redux';
import Helmet from 'react-helmet';

import Board from './components/Board';
import GroupFilterDropdown from './components/GroupFilterDropdown';
import LeaderboardTemplateCardShimmer from './components/LeaderboardTemplateCardShimmer';
import LeaderboardTemplatesGrid from './components/LeaderboardTemplatesGrid';
import LeaderboardZeroState from './components/LeaderboardZeroState';
import NoGroupsMessage from './components/NoGroupsMessage';
import LeaderboardModal from './modals/LeaderboardModal';

import Button from '../../shared/_TBComponents/Button';
import HeaderText from '../../shared/_TBComponents/HeaderText';
import Spinner from '../../shared/_TBComponents/Spinner';
import LeaderboardLayout from './LeaderboardLayout';
import useWindowSize from '../../shared/hooks/useWindowSize';

import {
  setModalOpen, fetchTemplates, fetchGroups, clearAllBoards, setActiveFilters, setTvMode,
} from './ducks/leaderboardActions';

import iconSet from '../../shared/images/teambuildr-selection.json';
import TV from '../../shared/images/TV.svg';
import mockLeaderboardTemplates from './mockLeaderboardTemplates';
import { BoxShadow, ColorWhite } from '../../shared/GlobalStyles';

const TV_MODE_BUTTON_BREAKPOINT = 1024;
const ADD_BOARD_BUTTON_BREAKPOINT = 768;

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 64px;
  position: relative;
`;

const HeaderRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 30px;
  margin-bottom: 15px;
  padding: 0 30px;
  animation: fadeIn 250ms ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const HeaderLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const BackArrow = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #444;
  flex-shrink: 0;

  &:hover {
    color: #000;
  }
`;

const HeaderRight = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const SearchBar = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  background: white;
  border-radius: 8px;
  padding: 0 14px;
  box-shadow: ${BoxShadow};
  flex: 1 1 auto;
  min-width: 0;
`;

const SearchIconWrapper = styled('span')`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const SearchInput = styled('input')`
  border: none;
  outline: none;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #444;
  background: transparent;
  width: 300px;
  min-width: 0;

  &::placeholder {
    color: #bbb;
  }
`;

const BorderedIconWrapper = styled('div')`
  cursor: pointer;
  transition: all 250ms ease-in-out;
  border: 1px solid #EEEEEE;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  box-sizing: border-box;
  background-color: ${ColorWhite};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 1px 3px 0px #3031331A;
  flex-shrink: 0;

  :hover {
    opacity: 0.7;
  }
`;

const TVIcon = styled('div')`
  height: 20px;
  width: 20px;
  background-image: url(${TV});
  background-repeat: no-repeat;
  background-size: 20px;
  background-position: center;
`;

const TVModeText = styled('span')`
  font-family: 'Nunito Sans';
  font-weight: 700;
  font-size: 14px;
  color: #444444;
  margin-left: 8px;
  white-space: nowrap;
`;

const ShimmerGrid = styled('div')`
  display: flex;
  flex-wrap: wrap;
  padding: 10px 20px;
`;

const SpinnerWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
`;

const BoardWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
  flex-grow: 1;
  min-height: 0;
  flex-basis: 100%; /* Fix for Safari flex box wrong height issue */
  padding: 0 30px 15px;
`;

const Leaderboard = () => {
  const leaderboardsReference = useSelector(
    (state) => state.leaderboard.data.leaderboardsReference,
  );
  const view = useSelector((state) => state.leaderboard.ui.view);
  const isTvMode = useSelector((state) => state.leaderboard.ui.isTvMode);
  const activeFilters = useSelector((state) => state.leaderboard.ui.activeFilters);
  const groups = useSelector((state) => state.leaderboard.data.groups);
  const isLoadingGroups = useSelector((state) => state.leaderboard.ui.isLoadingGroups);
  const templatesInitialized = useSelector((state) => state.leaderboard.ui.templatesInitialized);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const currentUserLoaded = !!currentUser && Object.keys(currentUser).length > 0;
  const isAthlete = currentUser?.admin === 0;
  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [groupsFetchStarted, setGroupsFetchStarted] = useState(false);

  const boards = Object.keys(leaderboardsReference).map((k) => leaderboardsReference[k]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (currentUser?.accountCode) {
      setGroupsFetchStarted(true);
      dispatch(fetchGroups(currentUser.accountCode));
    }
  }, [currentUser?.accountCode]);

  // Athletes can only create one-time (non-saved) leaderboards, so there's no
  // template list to fetch for them — the templates endpoint is coach-only.
  useEffect(() => {
    if (currentUser?.accountCode && !!groups.length && !isAthlete) dispatch(fetchTemplates());
  }, [currentUser?.accountCode, groups.length, isAthlete]);

  useEffect(() => {
    if (view === 'home' && groups.length > 0 && !isAthlete) dispatch(fetchTemplates());
  }, [view, isAthlete]);

  useEffect(() => {
    if (view !== 'leaderboard' && isTvMode) dispatch(setTvMode(false));
  }, [view, isTvMode]);

  // Enforces fullscreen only available while in TV mode
  useEffect(() => {
    if (!isTvMode && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  }, [isTvMode]);

  useEffect(() => () => {
    dispatch(setTvMode(false));
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  }, []);

  const handleEnterTvMode = () => {
    dispatch(setTvMode(true));
  };

  const handleSetModalOpen = (modalOpen, modal, boardId) => {
    dispatch(setModalOpen(modalOpen, modal, boardId));
  };

  const addBoard = () => {
    handleSetModalOpen(true, 'Add Leaderboard');
  };

  const shimmerGrid = (
    <ShimmerGrid>
      {mockLeaderboardTemplates.map((t) => (
        <LeaderboardTemplateCardShimmer key={t.id} />
      ))}
    </ShimmerGrid>
  );

  const spinner = (
    <SpinnerWrapper>
      <Spinner />
    </SpinnerWrapper>
  );

  const renderHomeContent = () => {
    // Role is unknown until currentUser resolves — default to the neutral
    // spinner rather than risk showing the coach-oriented shimmer to an athlete.
    if (!currentUserLoaded) return spinner;
    if (!groupsFetchStarted || (isLoadingGroups && !groups.length)) {
      return isAthlete ? spinner : shimmerGrid;
    }
    if (groups.length === 0) return <NoGroupsMessage />;
    if (isAthlete) {
      return (
        <LeaderboardZeroState
          heading='Where do you rank?'
          subtext='Build a leaderboard for any lift, SAQ+C event, or circuit to compare your performance against your peers.'
          buttonLabel='+ Create Leaderboard'
          onButtonClick={() => handleSetModalOpen(true, 'Create Leaderboard')}
        />
      );
    }
    if (!templatesInitialized) return shimmerGrid;
    return <LeaderboardTemplatesGrid searchTerm={debouncedSearchTerm} />;
  };

  return (
    <LeaderboardLayout fullWidth>
      <PageWrapper>
        <Helmet>
          <title>Leaderboard | TeamBuildr</title>
        </Helmet>

        {!isTvMode && (
          <HeaderRow>
            <HeaderLeft>
              {view === 'leaderboard' && (
                <BackArrow onClick={() => dispatch(clearAllBoards())}>
                  <IcomoonReact iconSet={iconSet} size={16} icon='left-arrow' color='currentColor' />
                </BackArrow>
              )}
              <HeaderText fontWeight='300' letterSpacing='normal'>
                Leaderboard
              </HeaderText>
            </HeaderLeft>

            {view === 'home' && groupsFetchStarted && !isLoadingGroups && !isAthlete && (
              <HeaderRight>
                <SearchBar>
                  <SearchIconWrapper>
                    <IcomoonReact iconSet={iconSet} size={16} icon='magnifying-glass-alt' color='black' />
                  </SearchIconWrapper>
                  <SearchInput
                    placeholder='Search by exercise...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SearchBar>
                <GroupFilterDropdown
                  groups={groups}
                  height={40}
                  selectedGroups={activeFilters.groups}
                  onChange={(newGroups) => dispatch(setActiveFilters({
                    ...activeFilters,
                    groups: newGroups,
                  }))}
                />
              </HeaderRight>
            )}
            {view === 'leaderboard' && (
              <HeaderRight>
                {windowSize.width >= TV_MODE_BUTTON_BREAKPOINT && (
                  <BorderedIconWrapper
                    onClick={handleEnterTvMode}
                    aria-label='Enter TV Mode'
                  >
                    <TVIcon />
                    <TVModeText>TV Mode</TVModeText>
                  </BorderedIconWrapper>
                )}
                {boards.length !== 3 && windowSize.width >= ADD_BOARD_BUTTON_BREAKPOINT && (
                  <Button
                    name='+ Add Board'
                    bgColor='#444444'
                    txtColor='white'
                    noBorder
                    customBorderRadius='8px'
                    styledTextProps={{ fontWeight: 700, fontSize: 14, letterSpacing: '0px' }}
                    styledWrapperProps={{ height: '40px', padding: '0 16px' }}
                    onClick={addBoard}
                  />
                )}
              </HeaderRight>
            )}
          </HeaderRow>
        )}

        {view === 'home' && renderHomeContent()}
        {view === 'leaderboard' && (
          <BoardWrapper>
            {boards.map((board) => (
              <Board
                activeBoards={boards.length}
                id={board.id}
                key={board.id}
              />
            ))}
          </BoardWrapper>
        )}

        <LeaderboardModal />
      </PageWrapper>
    </LeaderboardLayout>
  );
};

export default Leaderboard;
